<template>
	<div
		ref="toaster"
		class="flow-toaster"
		:style="styleObj"
		:data-state="state"
		:data-position="position"
		@mouseover="mouseover = true"
		@mouseleave="mouseover = false"
	>
		<div class="flow-close-toaster" @click="remove()">
			<Icon name="i-close" type="filled" state="dark" size="xx-small" :effects="false" />
		</div>

		<slot />
	</div>
</template>
<script>
import { defineComponent } from "vue";
import ToastQueue from "./ToastQueue";
import Icon from "./../Icon/Icon.vue";

export default defineComponent({
	name: "Toast",
	components: {
		Icon
	},
	props: {
		/**
		 * How many seconds it will show
		 * <br/>Duration in seconds
		 */
		duration: {
			type: Number,
			default: 3
		},
		/**
		 * true | false <br/>
		 * true : It will be removed automatically after specified duration<br/>
		 * false : User has to manually remove Toaster.
		 */
		autoRemove: {
			type: Boolean,
			default: true
		},
		/**
		 * 	'default' | 'primary' | 'primary-light' | 'primary-dark' |'success' | 'error' | 'error-light' | 'error-dark' | 'warning' | 'warning-light' | 'warning-dark' | 'success-light' | 'success-dark' | 'light' | 'dark' | 'border' | 'element' | 'avatar'
		 */
		state: {
			type: String,
			default: "default",
			validator(value) {
				return [
					"default",
					"primary",
					"primary-light",
					"primary-dark",
					"success",
					"error",
					"error-light",
					"error-dark",
					"warning",
					"warning-light",
					"warning-dark",
					"success-light",
					"success-dark",
					"light",
					"dark",
					"border",
					"element"
				].includes(value);
			}
		},
		id: {
			type: String,
			default: null
		},
		position: {
			type: String,
			default: "fixed",
			validator(value) {
				return ["fixed", "absolute", "relative"].includes(value);
			}
		}
	},
	data() {
		return {
			top: "0px",
			width: 412,
			height: 108,
			right: `-${this.width}px`,
			transform: "translateX(120%)",
			mouseover: false
		};
	},
	computed: {
		styleObj() {
			if (this.position === "relative") {
				return {
					transform: this.transform
				};
			} else {
				return {
					top: `${this.top}px`,
					right: `${this.right}px`
				};
			}
		},
		uid() {
			return this.id ? this.id : this.generateId();
		}
	},
	watch: {
		autoRemove: {
			handler() {
				this.autoRemoveConfig();
			}
		}
	},
	mounted() {
		this.height = this.$refs.toaster.scrollHeight;
		this.$nextTick(() => {
			ToastQueue.add(this);
			this.autoRemoveConfig();
		});
	},
	methods: {
		setPosition(top, right, transform = "translateX(0%)") {
			if (this.position === "relative") {
				this.transform = transform;
			} else {
				this.top = top;
				this.right = right;
			}
		},
		remove() {
			/**
			remove event
			 */
			this.$emit("remove", this.uid);
			ToastQueue.remove(this, () => this.$emit("removed", this.uid));
		},
		autoRemoveConfig() {
			if (!this.autoRemove) {
				return;
			}

			setTimeout(() => {
				if (!this.mouseover) {
					this.remove();
				} else {
					this.autoRemoveConfig();
				}
			}, this.duration * 1000);
		},
		generateId(length = 5) {
			let result = "";
			const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
			const charactersLength = characters.length;
			for (let i = 0; i < length; i++) {
				result += characters.charAt(Math.floor(Math.random() * charactersLength));
			}
			return result;
		}
	}
});
</script>
<style lang="scss">
$states: (
	"default": var(--dashboard-element),
	"primary": var(--dashboard-primary),
	"primary-light": var(--dashboard-primary-light),
	"primary-dark": var(--dashboard-primary-dark),
	"success": var(--dashboard-success),
	"success-light": var(--dashboard-success-light),
	"success-dark": var(--dashboard-success-dark),
	"error": var(--dashboard-danger),
	"error-light": var(--dashboard-danger-light),
	"error-dark": var(--dashboard-danger-dark),
	"warning": var(--dashboard-warning),
	"warning-light": var(--dashboard-warning-light),
	"warning-dark": var(--dashboard-warning-dark),
	"light": var(--dashboard-element-light),
	"dark": var(--dashboard-element-dark),
	"element": var(--dashboard-element),
	"border": var(--dashboard-border-light)
);
div.flow-toaster {
	@each $state, $color in $states {
		&[data-state="#{$state}"] {
			background-color: $color;
		}
	}

	display: flex;

	height: fit-content;
	min-height: 80px;
	width: 412px;
	border-radius: 4px;

	z-index: 105;

	transition: right 0.3s ease-out, top 0.2s ease-out, transform 0.3s ease-out;
	overflow: visible;

	&[data-position="fixed"] {
		position: fixed;
		right: -412px;
		top: 12px;
	}
	&[data-position="absolute"] {
		position: absolute;
		right: -412px;
		top: 12px;
	}
	&[data-position="relative"] {
		position: relative;
		transform: translateX(120%);
	}
	&:hover {
		.flow-close-toaster {
			opacity: 1;
		}
	}

	.flow-close-toaster {
		position: absolute;
		top: -10px;
		left: -8px;
		opacity: 0;
		z-index: 106;
		transition: opacity 0.3s linear;

		width: 24px;
		height: 24px;

		border-radius: 50%;

		background-color: var(--dashboard-border-dark);

		display: inline-flex;
		align-items: center;
		justify-content: center;

		cursor: pointer;

		&:hover {
			background-color: var(--dashboard-primary);
		}
	}
}
</style>
